import { disbursementStatusText, isPaidDisbursement } from "./disbursements.js";
import { emailClientName, senderName } from "./emailIntake.js";
import { normalizePaymentStatus, paymentStatusLabel } from "./clerkStatements.js";

// "Submitted invoices": what a firm's staff sent in - emailed fee slips, invoices and
// disbursements, and travel claims - and whether each has been paid (Emma, 30 Oct 2026).
// Each row says who sent it, so a person can narrow the list to their own.

const KIND_LABEL = { fee_slip: "Fee slip", check_type: "Invoice", disbursement: "Disbursement" };
const lower = (value) => String(value || "").trim().toLowerCase();

export function senderEmail(sender = "") {
  const match = String(sender || "").match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  return match ? match[0].toLowerCase() : "";
}

// Paid, part paid or not yet: what the person who sent it wants to know.
function paidState(status) {
  if (status === "paid") return "paid";
  if (status === "partial_paid") return "part";
  return "open";
}

export function submittedRows({ intake = [], disbursements = [] } = {}) {
  const byEmail = new Map();
  for (const item of disbursements) {
    for (const id of item.emailIds || []) byEmail.set(String(id), item);
  }

  const rows = intake.map((item) => {
    const base = {
      id: `email-${item.id}`,
      kind: KIND_LABEL[item.kind] || "Invoice",
      clientName: emailClientName(item) || "Client not yet named",
      from: senderName(item.sender) || "",
      fromEmail: senderEmail(item.sender),
      receivedAt: String(item.received_at || item.created_at || ""),
      amount: Number(item.amount) || 0,
      reference: item.invoice_ref || item.file_name || item.subject || "",
    };
    if (item.kind === "disbursement") {
      const entered = byEmail.get(String(item.id));
      if (!entered) return { ...base, statusText: "Received - not yet entered", paid: "open" };
      return {
        ...base,
        amount: entered.amount || base.amount,
        statusText: disbursementStatusText(entered),
        paid: isPaidDisbursement(entered) ? "paid" : entered.status === "partial_paid" ? "part" : "open",
        paidOn: entered.paidOn || "",
      };
    }
    const status = normalizePaymentStatus(item.status);
    return { ...base, statusText: paymentStatusLabel(status), paid: paidState(status) };
  });

  for (const item of disbursements) {
    if (item.source !== "travel_claim") continue;
    rows.push({
      id: item.id,
      kind: "Travel claim",
      clientName: item.clientName || "",
      from: item.uploadedBy || String(item.supplier || "").replace(/^Travel claim - /, ""),
      fromEmail: "",
      receivedAt: item.uploadedAt || "",
      amount: item.amount || 0,
      reference: item.description || "",
      statusText: disbursementStatusText(item),
      paid: isPaidDisbursement(item) ? "paid" : item.status === "partial_paid" ? "part" : "open",
      paidOn: item.paidOn || "",
    });
  }

  return rows.sort((a, b) => String(b.receivedAt).localeCompare(String(a.receivedAt)));
}

// Sent by this person: their email address on an emailed item, their name on a travel claim.
export function isMine(row, user = {}) {
  const email = lower(user.email);
  if (row.fromEmail) return Boolean(email) && row.fromEmail === email;
  const names = [user.full_name, user.name].map(lower).filter(Boolean);
  return names.includes(lower(row.from));
}

// Claimed through ATLAS, so VLA pays the firm, and the firm still has to pay the provider
// (Emma, 30 Oct 2026): a disbursement or travel claim marked "Claim submitted in ATLAS", or
// one whose payment has been asked of Finance but is not yet marked paid.
export const AWAITING_PROVIDER_STATUSES = ["claim_submitted", "finance_emailed"];

export function awaitingProviderPayment(items = []) {
  return items.filter((item) => AWAITING_PROVIDER_STATUSES.includes(item.status) && !isPaidDisbursement(item));
}
